'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LuMenu,
  LuChevronLeft,
  LuChevronRight,
  LuChevronDown,
  LuLogOut,
  LuUser,
} from 'react-icons/lu';

import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Badge,
  Drawer,
  useBreakpointValue,
  type BoxProps,
} from '../index';
import { NavigationItem, navigationItems } from '../navigation-config';
import { useSwipeGesture } from '../hooks/use-swipe-gesture';
import { useTouchDevice, useHoverCapable } from '../hooks/use-responsive';
import { isPathActive } from '../utils/pathname.utils';

import { IconButton } from './icon-button';
import { Button } from './button';
import { Avatar } from './avatar/avatar';

// Framer Motion animasyon bileşenleri

export interface SidebarProps extends Omit<
  BoxProps,
  | 'onMouseDown'
  | 'onMouseMove'
  | 'onMouseUp'
  | 'onMouseLeave'
  | 'onTouchStart'
  | 'onTouchMove'
  | 'onTouchEnd'
> {
  open?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
  onCollapse?: () => void;
  pathname?: string;
  onNavigate?: (href: string) => void;
  /**
   * Fired on hover/touch-start of any navigation item with `href`.
   * Apps wire this to `router.prefetch(...)` so chunks + RSC payload
   * load while the user is still moving the cursor toward the item —
   * by the time `onNavigate` fires, the route is in cache. Idempotent
   * on Next.js's side (calls dedupe), so no rate limit needed.
   */
  onItemPrefetch?: (href: string) => void;
  user?: {
    name: string;
    email: string;
    avatar?: string;
    role?: string;
  };
  onLogout?: () => void;
  navigationItems?: NavigationItem[];
  showUserSection?: boolean;
  colorPalette?: string;
  swipeThreshold?: number;
  enableSwipeGestures?: boolean;
  title?: string;
}

function SidebarComponent({
  open = true,
  collapsed = false,
  onToggle,
  onCollapse,
  pathname = '',
  onNavigate,
  onItemPrefetch,
  user,
  onLogout,
  navigationItems: customItems,
  showUserSection = true,
  colorPalette = 'gray',
  swipeThreshold = 50,
  enableSwipeGestures = true,
  title,
  ...props
}: SidebarProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isTouchDevice = useTouchDevice();
  const isHoverCapable = useHoverCapable();
  const items = customItems || navigationItems;
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());

  // Auto-expand parent items if child or grandchild is active
  React.useEffect(() => {
    const newExpanded = new Set<string>();
    items.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          (child) =>
            isPathActive(child.href, pathname) ||
            child.children?.some((gc) => isPathActive(gc.href, pathname))
        );
        if (hasActiveChild) {
          newExpanded.add(item.id);
          // Auto-expand child that has active grandchildren
          item.children.forEach((child) => {
            if (child.children?.some((gc) => isPathActive(gc.href, pathname))) {
              newExpanded.add(child.id);
            }
          });
        }
      }
    });
    setExpandedItems(newExpanded);
  }, [pathname, items]);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleNavigate = (href: string) => {
    if (onNavigate) {
      onNavigate(href);
      if (isMobile && onToggle) {
        onToggle();
      }
    }
  };

  // Hover-triggered prefetch — gated on `(hover: hover) and (pointer:
  // fine)` so hybrid devices (Surface laptop, iPad + trackpad) still
  // get the desktop hover-prefetch path. Pure-touch tablets/phones
  // skip — their `mouseenter` fires on tap, ms before `onClick`, so
  // the prefetch race buys nothing.
  const handlePrefetch = (href: string | undefined) => {
    if (!href || !isHoverCapable) return;
    onItemPrefetch?.(href);
  };

  // Setup swipe gestures for mobile
  const swipeHandlers = useSwipeGesture(
    {
      onSwipeLeft: () => {
        if (isMobile && enableSwipeGestures && open && onToggle) {
          onToggle(); // Close sidebar on swipe left
        } else if (!isMobile && enableSwipeGestures && !collapsed && onCollapse) {
          onCollapse(); // Collapse sidebar on swipe left (desktop)
        }
      },
      onSwipeRight: () => {
        if (isMobile && enableSwipeGestures && !open && onToggle) {
          onToggle(); // Open sidebar on swipe right
        } else if (!isMobile && enableSwipeGestures && collapsed && onCollapse) {
          onCollapse(); // Expand sidebar on swipe right (desktop)
        }
      },
    },
    {
      threshold: swipeThreshold,
      horizontal: true,
      vertical: false,
      preventDefault: false, // Allow normal scrolling
    }
  );

  // Extract only the gesture handlers, not the state
  const gestureProps =
    isTouchDevice && enableSwipeGestures
      ? {
          onTouchStart: swipeHandlers.onTouchStart,
          onTouchMove: swipeHandlers.onTouchMove,
          onTouchEnd: swipeHandlers.onTouchEnd,
        }
      : {};

  const sidebarContent = (
    <Box
      display="flex"
      flexDirection="column"
      height="full"
      backgroundColor="white"
      borderRightWidth={!isMobile ? '1px' : '0'}
      borderColor="#E5E7EB"
      {...gestureProps}
      {...props}
    >
      {/* Header */}
      <Flex
        paddingX={collapsed ? 2 : 4}
        paddingY={4}
        alignItems="center"
        justifyContent={collapsed ? 'center' : 'space-between'}
        borderBottomWidth="1px"
        borderColor="#E5E7EB"
      >
        <AnimatePresence mode="wait">
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <Text fontSize="xl" fontWeight="bold" color="#0066CC">
                {title || 'TMS'}
              </Text>
            </motion.div>
          )}
        </AnimatePresence>
        {!isMobile && onCollapse && (
          <IconButton
            size={isMobile ? 'md' : 'sm'}
            variant="ghost"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={onCollapse}
            icon={collapsed ? <LuChevronRight /> : <LuChevronLeft />}
          />
        )}
      </Flex>

      {/* Navigation Items */}
      <VStack
        flex="1"
        overflowY="auto"
        paddingY={4}
        paddingX={collapsed ? 2 : 3}
        gap={1}
        align="stretch"
      >
        {items.map((item) => {
          if (item.divider) {
            return <Box key={item.id} marginY={2} borderTopWidth="1px" borderColor="#E5E7EB" />;
          }

          if (item.sectionLabel) {
            return (
              <Box key={item.id} marginTop={4} marginBottom={1} paddingX={collapsed ? 0 : 1}>
                {!collapsed && (
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="#9CA3AF"
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {item.label}
                  </Text>
                )}
                {collapsed && <Box marginY={1} borderTopWidth="1px" borderColor="#E5E7EB" />}
              </Box>
            );
          }

          const isActive = isPathActive(item.href, pathname);
          const isExpanded = expandedItems.has(item.id);
          const hasChildren = item.children && item.children.length > 0;
          const Icon = item.icon;

          return (
            <Box key={item.id}>
              <Button
                variant={isActive ? 'primary' : 'ghost'}
                colorPalette={isActive ? colorPalette : 'gray'}
                fullWidth
                size={isMobile ? 'lg' : 'sm'}
                onMouseEnter={() => handlePrefetch(item.href)}
                onClick={() => {
                  if (item.href) {
                    handleNavigate(item.href);
                  } else if (hasChildren) {
                    toggleExpanded(item.id);
                  }
                }}
              >
                <HStack gap={3} width="full">
                  {Icon && <Icon size={20} />}
                  {!collapsed && (
                    <>
                      <Text flex="1" textAlign="start">
                        {item.label}
                      </Text>
                      {item.badge && (
                        <Badge
                          size="sm"
                          colorPalette={
                            (item.badge.colorPalette || 'gray') as
                              | 'gray'
                              | 'red'
                              | 'green'
                              | 'blue'
                              | 'orange'
                              | 'yellow'
                              | 'purple'
                              | 'brand'
                          }
                        >
                          {item.badge.content}
                        </Badge>
                      )}
                    </>
                  )}
                  {!collapsed && hasChildren && (
                    <Box
                      as="span"
                      transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                      transition="transform 0.2s"
                      marginInlineStart="auto"
                    >
                      <LuChevronDown size={16} />
                    </Box>
                  )}
                </HStack>
              </Button>

              {/* Nested Items */}
              {!collapsed && hasChildren && (
                <AnimatePresence>
                  {isExpanded && (
                    <Box overflow="hidden">
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <VStack
                          marginTop={1}
                          marginInlineStart={Icon ? 8 : 4}
                          gap={1}
                          align="stretch"
                        >
                          {item.children!.map((child) => {
                            // Use exact match if this child's href is a prefix of any sibling href
                            // (e.g. /reports vs /reports/designer — only Designer should highlight)
                            const useExactMatch = item.children!.some(
                              (sibling) =>
                                sibling.id !== child.id &&
                                sibling.href?.startsWith(child.href + '/')
                            );
                            const isChildActive = isPathActive(child.href, pathname, useExactMatch);
                            const isChildExpanded = expandedItems.has(child.id);
                            const hasGrandchildren = child.children && child.children.length > 0;
                            const ChildIcon = child.icon;

                            return (
                              <Box key={child.id}>
                                <Button
                                  variant={isChildActive ? 'secondary' : 'ghost'}
                                  colorPalette={isChildActive ? colorPalette : 'gray'}
                                  fullWidth
                                  size={isMobile ? 'lg' : 'sm'}
                                  onMouseEnter={() => handlePrefetch(child.href)}
                                  onClick={() => {
                                    if (child.href) {
                                      handleNavigate(child.href);
                                    } else if (hasGrandchildren) {
                                      toggleExpanded(child.id);
                                    }
                                  }}
                                >
                                  <HStack gap={3} width="full">
                                    {ChildIcon && <ChildIcon size={16} />}
                                    <Text flex="1" textAlign="start">
                                      {child.label}
                                    </Text>
                                    {child.badge && (
                                      <Badge
                                        size="sm"
                                        colorPalette={
                                          (child.badge.colorPalette || 'gray') as
                                            | 'gray'
                                            | 'red'
                                            | 'green'
                                            | 'blue'
                                            | 'orange'
                                            | 'yellow'
                                            | 'purple'
                                            | 'brand'
                                        }
                                      >
                                        {child.badge.content}
                                      </Badge>
                                    )}
                                    {hasGrandchildren && (
                                      <Box
                                        as="span"
                                        transform={
                                          isChildExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                        }
                                        transition="transform 0.2s"
                                        marginInlineStart="auto"
                                      >
                                        <LuChevronDown size={14} />
                                      </Box>
                                    )}
                                  </HStack>
                                </Button>

                                {/* Third level - Grandchildren */}
                                {hasGrandchildren && (
                                  <AnimatePresence>
                                    {isChildExpanded && (
                                      <Box overflow="hidden">
                                        <motion.div
                                          initial={{ opacity: 0, height: 0 }}
                                          animate={{ opacity: 1, height: 'auto' }}
                                          exit={{ opacity: 0, height: 0 }}
                                          transition={{ duration: 0.2 }}
                                        >
                                          <VStack
                                            marginTop={1}
                                            marginInlineStart={ChildIcon ? 6 : 4}
                                            gap={1}
                                            align="stretch"
                                          >
                                            {child.children!.map((grandchild) => {
                                              const isGrandchildActive = isPathActive(
                                                grandchild.href,
                                                pathname
                                              );
                                              const GrandchildIcon = grandchild.icon;

                                              return (
                                                <Button
                                                  key={grandchild.id}
                                                  variant={
                                                    isGrandchildActive ? 'secondary' : 'ghost'
                                                  }
                                                  colorPalette={
                                                    isGrandchildActive ? colorPalette : 'gray'
                                                  }
                                                  fullWidth
                                                  size={isMobile ? 'lg' : 'xs'}
                                                  onMouseEnter={() =>
                                                    handlePrefetch(grandchild.href)
                                                  }
                                                  onClick={() =>
                                                    grandchild.href &&
                                                    handleNavigate(grandchild.href)
                                                  }
                                                >
                                                  <HStack gap={2} width="full">
                                                    {GrandchildIcon && <GrandchildIcon size={14} />}
                                                    <Text fontSize="sm">{grandchild.label}</Text>
                                                  </HStack>
                                                </Button>
                                              );
                                            })}
                                          </VStack>
                                        </motion.div>
                                      </Box>
                                    )}
                                  </AnimatePresence>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>
                      </motion.div>
                    </Box>
                  )}
                </AnimatePresence>
              )}
            </Box>
          );
        })}
      </VStack>

      {/* User Section */}
      {showUserSection && user && (
        <Box paddingX={collapsed ? 2 : 3} paddingY={4} borderTopWidth="1px" borderColor="#E5E7EB">
          {collapsed ? (
            <VStack gap={2}>
              <Avatar size="sm" src={user.avatar} name={user.name} />
              <IconButton
                size="sm"
                variant="ghost"
                aria-label="Logout"
                onClick={onLogout}
                icon={<LuLogOut />}
              />
            </VStack>
          ) : (
            <VStack gap={3} align="stretch">
              <HStack gap={3}>
                <Avatar size="sm" src={user.avatar} name={user.name} />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" lineHeight="1.2">
                    {user.name}
                  </Text>
                  <Text fontSize="xs" color="#6B7280" lineHeight="1.2">
                    {user.role || user.email}
                  </Text>
                </Box>
              </HStack>
              <HStack gap={2}>
                <Button size="sm" variant="ghost" fullWidth>
                  <LuUser size={16} />
                  <Text as="span" marginInlineStart={2}>
                    Profile
                  </Text>
                </Button>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Logout"
                  onClick={onLogout}
                  icon={<LuLogOut />}
                />
              </HStack>
            </VStack>
          )}
        </Box>
      )}
    </Box>
  );

  // Mobile Drawer
  if (isMobile) {
    return (
      <>
        {!open && (
          <Box position="fixed" top={4} left={4} zIndex="docked">
            <IconButton
              aria-label="Open menu"
              variant="primary"
              colorPalette={colorPalette}
              onClick={onToggle}
              size="lg"
              icon={<LuMenu />}
            />
          </Box>
        )}

        <Drawer.Root open={open}>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Body padding={0}>{sidebarContent}</Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Root>
      </>
    );
  }

  // Desktop Sidebar
  return (
    <Box
      as="aside"
      position="sticky"
      top="0"
      height="100vh"
      width={collapsed ? '64px' : '280px'}
      transition="width 0.2s ease"
      flexShrink={0}
    >
      {sidebarContent}
    </Box>
  );
}

// Memoized Sidebar export for performance optimization
export const Sidebar = React.memo(SidebarComponent);

// Simple navigation item component for custom implementations
export interface NavigationItemComponentProps {
  item: NavigationItem;
  isActive?: boolean;
  collapsed?: boolean;
  level?: number;
  onClick?: () => void;
}

function NavigationItemComponentBase({
  item,
  isActive = false,
  collapsed = false,
  level = 0,
  onClick,
}: NavigationItemComponentProps) {
  const Icon = item.icon;

  return (
    <Box marginLeft={level * 4} width="full">
      <Button
        variant={isActive ? 'primary' : 'ghost'}
        colorPalette={isActive ? 'blue' : 'gray'}
        fullWidth
        size="sm"
        onClick={onClick}
      >
        <HStack gap={3} width="full">
          {Icon && <Icon size={level > 0 ? 16 : 20} />}
          {!collapsed && (
            <>
              <Text flex="1" textAlign="left">
                {item.label}
              </Text>
              {item.badge && (
                <Badge
                  size="sm"
                  colorPalette={
                    (item.badge.colorPalette || 'gray') as
                      | 'gray'
                      | 'red'
                      | 'green'
                      | 'blue'
                      | 'orange'
                      | 'yellow'
                      | 'purple'
                      | 'brand'
                  }
                >
                  {item.badge.content}
                </Badge>
              )}
            </>
          )}
        </HStack>
      </Button>
    </Box>
  );
}

// Memoized NavigationItemComponent export
export const NavigationItemComponent = React.memo(NavigationItemComponentBase);
